import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  bulkProviderPackageAction,
  exportProviderWatermarkedPackages,
  importProviderWatermarkedPackageList,
  listProviderBatches,
} from '../../api/providerApi';
import {
  BulkPackageActions,
  bulkResultMessage,
  usePackageSelection,
} from '../../components/packages/BulkPackageActions';
import {
  eligibleExportIds,
  rememberEligibility,
} from '../../components/packages/watermarkedExport';
import { DataTable } from '../../components/ui/DataTable';
import { StatusPill } from '../../components/ui/StatusPill';
import { FilterTabs } from '../../components/ui/FilterTabs';
import { Pagination } from '../../components/ui/Pagination';
import { KpiCard } from '../../components/ui/KpiCard';
import { ErrorBanner } from '../../components/ui/ErrorBanner';
import { NoticeBanner } from '../../components/ui/NoticeBanner';
import { IconPackage, IconSearch } from '../../components/ui/icons';
import { formatDateTime } from '../../utils/format';
import {
  PACKAGE_STATUS_OPTIONS,
  authorityStateLabel,
  batchDisplayId,
  nextActionLabel,
  packageStatusLabel,
} from './providerReviewLabels';

const AUTHORITY_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'APPROVED', label: 'Approved' },
  { value: 'REJECTED', label: 'Rejected' },
];

// Column sort ids the list API accepts (see _batch_sort_column on the server).
const SORT_IDS = {
  batch: 'submit_date',
  program: 'program',
  submitter_org: 'submitter_org',
  package_status: 'package_status',
  authority_status: 'authority_status',
};

export function ProviderPackagesPage() {
  const [statusFilter, setStatusFilter] = useState('');
  const [authorityFilter, setAuthorityFilter] = useState('');
  const [programFilter, setProgramFilter] = useState('');
  const [submitterFilter, setSubmitterFilter] = useState('');
  const [sort, setSort] = useState({ by: 'submit_date', order: 'desc' });
  const [searchInputValue, setSearchInputValue] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [packages, setPackages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [eligibility, setEligibility] = useState({});
  const selection = usePackageSelection();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPage(1);
      setSearch(searchInputValue.trim());
    }, 400);
    return () => clearTimeout(timeout);
  }, [searchInputValue]);

  useEffect(() => {
    let cancelled = false;

    async function loadPackages() {
      setLoading(true);
      setError('');
      try {
        const response = await listProviderBatches({
          page,
          pageSize,
          search: search || undefined,
          status: statusFilter || undefined,
          authorityStatus: authorityFilter || undefined,
          program: programFilter || undefined,
          submitter: submitterFilter || undefined,
          sortBy: sort.by,
          sortOrder: sort.order,
        });
        if (cancelled) return;
        setPackages(response.items || []);
        setTotal(response.meta?.total || 0);
        setEligibility((current) =>
          rememberEligibility(current, response.items || [], (row) => row.batch_id)
        );
      } catch (requestError) {
        if (cancelled) return;
        setError(requestError.message || 'Unable to load packages.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadPackages();
    return () => {
      cancelled = true;
    };
  }, [
    page,
    pageSize,
    search,
    statusFilter,
    authorityFilter,
    programFilter,
    submitterFilter,
    sort,
    reloadToken,
  ]);

  // Column funnels: every change starts from page 1 and drops the selection,
  // since rows the user ticked may no longer be on screen.
  const clearSelection = selection.clear;
  const applyFilter = useCallback(
    (setter) => (value) => {
      setPage(1);
      clearSelection();
      setter(value);
    },
    [clearSelection]
  );

  const packageId = (row) => row.batch_id;
  const exportIds = eligibleExportIds(selection.selectedIds, eligibility);

  const handleBulk = async (action, mfa) => {
    const ids = Array.from(selection.selectedIds);
    setBusy(true);
    setActionError('');
    setNotice('');
    try {
      const response = await bulkProviderPackageAction(ids, action, mfa);
      const result = bulkResultMessage(response);
      setNotice(result.notice);
      setActionError(result.error);
      selection.retain(result.succeededIds);
      setReloadToken((value) => value + 1);
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async (mfa) => {
    setBusy(true);
    try {
      const result = await exportProviderWatermarkedPackages(exportIds, mfa);
      selection.clear();
      return result;
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (file, mfa) => {
    setBusy(true);
    try {
      return await importProviderWatermarkedPackageList(file, mfa);
    } finally {
      setBusy(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: 'batch',
        header: 'Package',
        sortId: SORT_IDS.batch,
        render: (row) => (
          <div className="min-w-0">
            <span className="block font-semibold text-eorig-ink">
              {batchDisplayId(row)}
            </span>
            <span className="block text-[11px] text-eorig-muted">
              {formatDateTime(row.submit_date)}
            </span>
          </div>
        ),
      },
      {
        key: 'program',
        header: 'Program',
        sortId: SORT_IDS.program,
        filter: {
          type: 'text',
          value: programFilter,
          placeholder: 'Programme name',
          ariaLabel: 'Filter by program',
          onChange: applyFilter(setProgramFilter),
        },
        render: (row) =>
          row.program ? (
            <span className="inline-flex max-w-[160px] truncate rounded-md bg-eorig-surface px-2 py-1 text-[11px] text-eorig-muted">
              {row.program}
            </span>
          ) : (
            <span className="text-eorig-muted">—</span>
          ),
      },
      {
        key: 'submitter_org',
        header: 'Submitter',
        sortId: SORT_IDS.submitter_org,
        filter: {
          type: 'text',
          value: submitterFilter,
          placeholder: 'Submitter name',
          ariaLabel: 'Filter by submitter',
          onChange: applyFilter(setSubmitterFilter),
        },
        render: (row) => row.submitter_org || '—',
      },
      {
        key: 'package_status',
        header: 'Status',
        sortId: SORT_IDS.package_status,
        filter: {
          type: 'select',
          value: statusFilter,
          options: PACKAGE_STATUS_OPTIONS,
          ariaLabel: 'Filter by status',
          onChange: applyFilter(setStatusFilter),
        },
        render: (row) => (
          <StatusPill value={row.package_status} label={packageStatusLabel(row.package_status)} />
        ),
      },
      {
        key: 'authority_status',
        header: 'Authority',
        sortId: SORT_IDS.authority_status,
        filter: {
          type: 'select',
          value: authorityFilter,
          options: AUTHORITY_OPTIONS,
          ariaLabel: 'Filter by authority',
          onChange: applyFilter(setAuthorityFilter),
        },
        render: (row) => (
          <StatusPill
            value={row.authority_status}
            label={authorityStateLabel(row.authority_status)}
          />
        ),
      },
      {
        key: 'next_action',
        header: 'Next action',
        // Derived from authority + custody on the server; filter on Authority instead.
        sortable: false,
        render: (row) => (
          <StatusPill value={row.next_action} label={nextActionLabel(row.next_action)} />
        ),
      },
    ],
    [applyFilter, statusFilter, authorityFilter, programFilter, submitterFilter]
  );

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Total packages"
          value={total}
          subline="Matching current filters"
          icon={IconPackage}
          accent
          loading={loading}
        />
      </div>

      <section className="space-y-3 rounded-2xl border border-eorig-border bg-white p-4 shadow-eorig-card">
        <FilterTabs
          tabs={PACKAGE_STATUS_OPTIONS}
          value={statusFilter}
          ariaLabel="Filter by status"
          onChange={(value) => {
            setPage(1);
            selection.clear();
            setStatusFilter(value);
          }}
        />
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-eorig-muted">
            <IconSearch className="h-4 w-4" />
          </span>
          <input
            value={searchInputValue}
            onChange={(event) => setSearchInputValue(event.target.value)}
            placeholder="Search by reference, program, or submitter"
            aria-label="Search packages"
            className="w-full rounded-full border border-eorig-border bg-white py-2 pl-9 pr-3 text-sm text-eorig-ink placeholder:text-eorig-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-eorig-cyan/35"
          />
        </div>
      </section>

      {error ? <ErrorBanner message={error} /> : null}
      {actionError ? <ErrorBanner message={actionError} /> : null}
      {notice ? <NoticeBanner>{notice}</NoticeBanner> : null}

      <BulkPackageActions
        role="provider"
        selectedCount={selection.count}
        exportCount={exportIds.length}
        busy={busy}
        onAction={handleBulk}
        onExport={handleExport}
        onImport={handleImport}
      />

      <DataTable
        columns={columns}
        rows={packages}
        rowKey={packageId}
        rowHref={(row) => `/provider/packages/${row.batch_id}`}
        loading={loading}
        emptyMessage="No packages found for selected filters."
        minWidth={720}
        sortKey={sort.by}
        sortDir={sort.order}
        onSortChange={(by, order) => {
          setPage(1);
          setSort({ by, order });
        }}
        selection={{
          isSelected: (row) => selection.selectedIds.has(row.batch_id),
          onToggle: (row) => selection.toggle(row.batch_id),
          onToggleAll: (rows, checked) => selection.toggleAll(rows, checked, packageId),
          label: (row) => `Select ${row.reference_number || row.batch_id}`,
        }}
      />

      <Pagination
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPage(1);
          setPageSize(size);
        }}
        disabled={loading}
      />
    </div>
  );
}
